import NetworkConnectivityException, { name as NETWORK_CONNECTIVITY } from '../Exception/NetworkConnectivityException';
import UnknownNetworkErrorException, { name as UNKNOWN_NETWORK } from '../Exception/UnknownNetworkErrorException';

/**
 * Exceptions are matched on `instanceof` AND on the name they set on themselves.
 *
 * Comparing `err.name` with `SomeException.name` (the static class name) looks
 * equivalent but silently breaks in production builds: terser mangles class
 * names, so `UnknownNetworkErrorException.name` becomes something like "e"
 * while the instance still carries the literal string. The name constants below
 * are string literals, so they survive minification.
 */
function hasName(error: any, expected: string): boolean {
  return !!error && error.name === expected;
}

/**
 * True only when the request never made it to the server. A 4xx/5xx answer is a
 * server response, not a connectivity problem.
 */
export function isConnectivityError(error: any): boolean {
  return error instanceof NetworkConnectivityException
    || error instanceof UnknownNetworkErrorException
    || hasName(error, NETWORK_CONNECTIVITY)
    || hasName(error, UNKNOWN_NETWORK);
}

/**
 * There is no connection and nothing copied to answer from: the one sentence the user gets is shown where this
 * is raised, so callers must let it through instead of reporting it again.
 */
export class OfflineError extends Error {
  constructor() {
    super('No data connection available');
    this.name = 'OfflineError';
  }
}

/**
 * A request that never reached the server (offline, DNS/TLS, CORS) is a connection problem 
 * like the one the app raises itself: not to be reported as a failure of the page
 */
export function isOfflineError(error: any): boolean {
  return error instanceof OfflineError
    || hasName(error, 'OfflineError')
    || hasName(error, 'NetworkConnectivityException');
}

/**
 * A message worth showing to the user: the server explanation when there is one,
 * never an empty string.
 */
export function describeError(error: any): string {
  if (!error) {
    return 'unknown error';
  }
  if (typeof error === 'string') {
    return error;
  }
  return error.message || String(error);
}
